import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { ComoAssetListBlockConfig } from '@front-platform/content-builder';
import { comoAssetListBlockResolveConfig } from '@front-platform/content-builder';
import { Component, OnDestroy, OnInit, inject, input } from '@angular/core';
import type { ComoAssetListBlockScenarioContent } from '@front-platform/content-builder';
import { COMO_ASSET_LIST_BLOCK_COMPONENT } from '@core/services/cb2-preview/cb2-preview-bloc-types';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import { previewScenarioData } from '@core/services/cb2-preview/preview-scenario-data.util';
import { previewScenarioForBloc$ } from '@core/services/cb2-preview/preview-scenario-for-bloc.util';
import { ComoService } from '@core/services/como/como.service';
import { Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { ComoAsset, ComoUser } from '@core/models/como.model';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { CartService } from '@core/services/cart/cart.service';
import { OrderStoreDispatcher } from '@common/dispatchers/order.dispatcher';
import { BasketService } from '../../../../core/services/basket/basket.service';
export interface ComoAssetListBlockRuntimeConfig {}

export type ComoAssetListBlockConfigInput = Partial<
  Omit<ComoAssetListBlockConfig, keyof ComoAssetListBlockRuntimeConfig> &
    ComoAssetListBlockRuntimeConfig
>;
export type ComoAssetListBlockResolvedConfig = Omit<
  ComoAssetListBlockConfig,
  keyof ComoAssetListBlockRuntimeConfig
> &
  Partial<ComoAssetListBlockRuntimeConfig>;
export interface ComoAssetListBlockContract extends ConfigComponentContract<
  ComoAssetListBlockConfigInput,
  ComoAssetListBlockResolvedConfig
> {}

@Component({
  selector: 'app-como-asset-list-block',
  templateUrl: './como-asset-list-block.component.html',
  standalone: false,
})
export class ComoAssetListBlockComponent implements OnInit, OnDestroy, ComoAssetListBlockContract {
  readonly config = input<ComoAssetListBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    ComoAssetListBlockConfig,
    ComoAssetListBlockRuntimeConfig
  >(comoAssetListBlockResolveConfig, () => ({ config: this.config() }));
  get heading(): string {
    return this.resolvedConfig()['heading'] as unknown as string;
  }
  get subheading(): string | undefined {
    return this.resolvedConfig()['subheading'] as unknown as string | undefined;
  }
  get displayCredits(): boolean {
    return this.resolvedConfig()['display_credits'] as unknown as boolean;
  }
  get displayPoints(): boolean {
    return this.resolvedConfig()['display_points'] as unknown as boolean;
  }
  get displayRedeemableGifts(): boolean {
    return this.resolvedConfig()['display_redeemable_gifts'] as unknown as boolean;
  }
  get displayNotRedeemableGifts(): boolean {
    return this.resolvedConfig()['display_not_redeemable_gifts'] as unknown as boolean;
  }
  get displayRedeemablePointShop(): boolean {
    return this.resolvedConfig()['display_redeemable_point_shop'] as unknown as boolean;
  }
  get displayNotRedeemablePointShop(): boolean {
    return this.resolvedConfig()['display_not_redeemable_point_shop'] as unknown as boolean;
  }
  get displayNotUsablePointShop(): boolean {
    return this.resolvedConfig()['display_not_usable_point_shop'] as unknown as boolean;
  }
  get backgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor;
  }

  public comoUser?: ComoUser | null;

  private readonly cb2PreviewState = inject(Cb2PreviewStateService);
  private readonly previewScenario$ = previewScenarioForBloc$(COMO_ASSET_LIST_BLOCK_COMPONENT);
  private readonly destroyed$ = new Subject<boolean>();

  constructor(
    private readonly comoService: ComoService,
    private readonly cartService: CartService,
    private readonly basketService: BasketService,
    private readonly orderDispatcher: OrderStoreDispatcher,
  ) {}

  ngOnInit(): void {
    if (this.cb2PreviewState.isActive()) {
      this.previewScenario$
        .pipe(
          takeUntil(this.destroyed$),
          tap(scenario => {
            const content = previewScenarioData<ComoAssetListBlockScenarioContent>(
              scenario,
              COMO_ASSET_LIST_BLOCK_COMPONENT,
            );
            this.comoUser = (content?.comoUser as ComoUser | null | undefined) ?? null;
          }),
        )
        .subscribe();
      return;
    }

    this.comoService.loadComoUser();
    this.comoService
      .getComoUser$()
      .pipe(
        takeUntil(this.destroyed$),
        tap(comoUser => (this.comoUser = comoUser)),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  onAssetAdd(asset: ComoAsset): void {
    this.comoService.confirmUsePointToAddAsset(asset);
  }

  onAssetRemove($event: ComoAsset): void {
    const coupon = { key: $event.key };
    this.cartService.removeCoupon(coupon);
    this.basketService
      .removeBasketCoupon$(coupon)
      .pipe(switchMap(() => this.orderDispatcher.checkCartIsValid$()))
      .subscribe();
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(ComoAssetListBlockComponent, { 'keyAliases': {} });
